import { useState } from 'react';
import { Link } from 'react-router-dom';
import { RecordsTable, type Column, type FilterDef } from '../components/RecordsTable/RecordsTable';
import { DeleteRowButton } from '../components/DeleteRowButton/DeleteRowButton';
import { mcpWrite } from '../lib/mcp';
import { useMcpTools } from '../lib/useMcpTools';
import { useToast } from '../components/Toast/ToastProvider';
import { money } from '../lib/format';
import './Settings.css';
import { pct } from '../lib/format';
import { zipToState } from '../lib/zipState';

interface DealerRow {
  id: number;
  company_name: string;
  license_number: string | null;
  primary_zip: string | null;
  program_tier: string;
  commission_rate: number | string; // pg numeric arrives as a string — coerce on display
  net_terms_days: number;
  status: string;
  // NOTE: listDealers does not return created_at yet — the "Date added" column below renders "—"
  // until the MCP read tool is extended (flagged as a cabana-mcp dependency in the PR).
  created_at?: string | null;
}

const fmtDate = (v: string | null | undefined) => (v ? new Date(v).toLocaleDateString() : '—');

const COLUMNS: Column<DealerRow>[] = [
  { key: 'company_name', header: 'Company', sortable: true, render: (r) => <Link className="records__link" to={`/dealers/${r.id}`}>{r.company_name || '—'}</Link> },
  { key: 'program_tier', header: 'Tier', sortable: true },
  {
    key: 'commission_rate',
    header: 'Commission',
    numeric: true,
    sortable: true,
    sortAccessor: (r) => Number(r.commission_rate),
    render: (r) => pct(r.commission_rate),
  },
  { key: 'net_terms_days', header: 'Net terms', numeric: true, render: (r) => `${r.net_terms_days}d` },
  // State is derived client-side from primary_zip (dealers have no state column in the MCP) —
  // an approximation; see lib/zipState.ts. Sorts by the derived state.
  {
    key: 'state',
    header: 'State',
    sortable: true,
    sortAccessor: (r) => zipToState(r.primary_zip),
    render: (r) => zipToState(r.primary_zip) ?? '—',
  },
  // B5: "date added" + sort. Built to scale — sorting is client-side over the fetched page today;
  // scaling past a few hundred rows will want server-side sort/pagination (cabana-mcp dep).
  {
    key: 'created_at',
    header: 'Date added',
    sortable: true,
    sortAccessor: (r) => r.created_at ?? null,
    render: (r) => fmtDate(r.created_at),
  },
  {
    key: 'status',
    header: 'Status',
    render: (r) => (
      <span className={`records__tag records__tag--${r.status === 'active' ? 'ok' : 'warn'}`}>{r.status}</span>
    ),
  },
];

const FILTERS: FilterDef<DealerRow>[] = [
  {
    key: 'status',
    label: 'Status',
    options: [
      { value: 'all', label: 'All' },
      { value: 'active', label: 'Active' },
      { value: 'inactive', label: 'Inactive' },
    ],
    match: (r, v) => (v === 'active' ? r.status === 'active' : r.status !== 'active'),
  },
];

function lastMonth(): string {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Run the monthly co-op accrual (backlog 506). The MCP action credits each dealer 1%
 * (system_settings.coop_earn_rate) of the subscription revenue attributed to them for the
 * chosen month, idempotent per dealer and month, so re-running is safe. A Cloud Scheduler
 * would call this on the 1st once prelaunch ends; until then this is how an Admin runs it.
 * Shows only for a role the MCP grants the action to.
 */
function CoopAccrualCard() {
  const { can } = useMcpTools();
  const { notify } = useToast();
  const [month, setMonth] = useState(lastMonth());
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ month: string; creditedUsd: number; dealers: unknown[] } | null>(null);
  if (!can('accrueCoopForMonth')) return null;

  async function run() {
    if (busy) return;
    setBusy(true);
    setResult(null);
    try {
      const r = await mcpWrite<{ month: string; creditedUsd: number; dealers: unknown[] }>('accrueCoopForMonth', { month });
      setResult(r);
      notify({ variant: 'success', message: `Co-op run for ${r.month}: ${money(r.creditedUsd)} credited across ${r.dealers.length} dealer(s).` });
    } catch (err) {
      notify({ variant: 'error', message: (err as Error).message });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="settings__list">
      <div className="settings__row">
        <div className="settings__meta">
          <code className="settings__key">Monthly co-op accrual</code>
          <p className="settings__desc">
            Credit each dealer 1% of the subscription revenue attributed to them for a closed month.
            Idempotent — a month already credited is skipped, so running it again is safe.
          </p>
          {result && (
            <p className="settings__stamp">
              {result.month}: {money(result.creditedUsd)} credited across {result.dealers.length} dealer(s).
            </p>
          )}
        </div>
        <div className="account__form">
          <input
            className="settings__input"
            type="month"
            value={month}
            max={lastMonth()}
            onChange={(e) => setMonth(e.target.value)}
            disabled={busy}
            aria-label="Month to accrue co-op for"
          />
          <button type="button" className="settings__save" disabled={busy || !/^\d{4}-(0[1-9]|1[0-2])$/.test(month)} onClick={run}>
            {busy ? 'Running…' : 'Run co-op for this month'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function Dealers() {
  return (
    <>
      <CoopAccrualCard />
      <RecordsTable<DealerRow>
      title="Dealers"
      intro="Wholesale dealer accounts and their program terms — tier, commission rate, net payment terms, and territory. These are the B2B partners who buy Cabana filters at wholesale and resell them."
      tool="listDealers"
      args={{ limit: 200 }}
      select={(r) => (r as { dealers: DealerRow[] }).dealers ?? []}
      columns={COLUMNS}
      rowKey={(r) => r.id}
      emptyMessage="No dealer accounts yet."
      refreshable
      searchable={{
        placeholder: 'Search company, state, ZIP, tier, or license…',
        match: (r, q) =>
          [r.company_name, zipToState(r.primary_zip), r.primary_zip, r.program_tier, r.license_number].some(
            (f) => f != null && f.toLowerCase().includes(q),
          ),
      }}
      filters={FILTERS}
      rowClassName={(r) => (r.status !== 'active' ? 'records__row--inactive' : undefined)}
      /* Backlog 1033. The button hides itself unless the operator's role holds the grant,
         and the TOOL refuses any dealer with dependent records — sixteen tables reference
         `dealers` and four of them cascade, so this is the only thing standing between a
         misclick and a silently deleted co-op balance. Deactivate is the control for a
         dealer who has actually traded. */
      actions={{
        header: '',
        render: (r) => (
          <DeleteRowButton
            row={r}
            action="deleteDealer"
            noun="dealer"
            label={r.company_name}
            invalidate={['mcp', 'listDealers']}
          />
        ),
      }}
      />
    </>
  );
}
